import { defineStore } from 'pinia'
import { apiService } from '@/services/api'

// キューアイテムの状態
export const QUEUE_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
  CANCELLED: 'cancelled',
}

// キューアイテムの種類
export const QUEUE_TYPES = {
  UPLOAD: 'upload',
  DELETE: 'delete',
  RENAME: 'rename',
}

// エラータイプ
export const ERROR_TYPES = {
  NETWORK: 'network',
  SERVER: 'server',
  VALIDATION: 'validation',
  CANCELLED: 'cancelled',
}

// キューアイテムクラス
class QueueItem {
  constructor(type, data) {
    this.id = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    this.type = type
    this.status = QUEUE_STATUS.PENDING
    this.data = data
    this.progress = 0
    this.error = null
    this.retryCount = 0
    this.createdAt = new Date()
    this.startedAt = null
    this.completedAt = null
    this.estimatedTime = null
    this.actualTime = null
  }
}

export const useStorageStore = defineStore('storage', {
  state: () => ({
    files: [],
    currentPath: '',
    pathParts: [],
    uploadProgress: 0, // 廃止予定（キューシステムで置き換え）
    selectedFiles: [],
    sortBy: 'name',
    sortOrder: 'asc',
    companyUuid: '',
    tableList: [],
    companyName: '',

    // キューシステム
    taskQueue: [],
    isQueueProcessing: false,
    maxConcurrentTasks: 1,
    currentProcessingTasks: [],

    // キュー統計
    queueStats: {
      total: 0,
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0,
      cancelled: 0,
    },

    // UI状態
    queueUIVisible: false,
    queueUIExpanded: false,

    // 設定
    queueSettings: {
      autoRetry: true,
      maxRetries: 3,
      retryDelay: 2000,
    },
  }),

  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        {
          label: 'ファイル名',
          key: 'name',
          data: (item, value) => value,
        },
        {
          label: '更新日時',
          key: 'updatedAt',
          data: (item, value) => this.formatDate(value),
        },
        {
          label: 'サイズ',
          key: 'size',
          data: (item, value) => (item.type === 'folder' ? '-' : this.formatFileSize(value)),
        },
        {
          label: '種類',
          key: 'type',
          data: (item, value) => this.getFileType(item),
        },
        {
          label: '移動',
          key: 'navigate',
          data: (item, value) => (item.type === 'folder' ? 'フォルダを開く' : '-'),
          action: async (table, item, event) => {
            event.stopPropagation()
            const newPath = this.currentPath ? `${this.currentPath}/${item.name}` : item.name
            await this.navigateToPath(newPath)
          },
          actionEnabled: (table, item) => item.type === 'folder',
          css: 'btn btn-primary',
        },
        {
          label: 'ダウンロード',
          key: 'download',
          data: (item, value) => (item.type !== 'folder' ? 'ダウンロード' : '-'),
          action: (table, item, event) => {
            event.stopPropagation()
            // ダウンロード処理はコンポーネント側で実装
          },
          actionEnabled: (table, item) => item.type !== 'folder',
          css: 'btn',
        },
        {
          label: '名前変更',
          key: 'rename',
          data: (item, value) => '名前変更',
          action: (table, item, event) => {
            event.stopPropagation()
            // 名前変更処理はコンポーネント側で実装
          },
          actionEnabled: (table, item) => true,
          css: 'btn',
        },
        {
          label: '削除',
          key: 'delete',
          data: (item, value) => '削除',
          action: (table, item, event) => {
            event.stopPropagation()
            // 削除処理はコンポーネント側で実装
          },
          actionEnabled: (table, item) => true,
          css: 'btn-alert',
        },
      ]
    },

    // 会社UUIDを設定
    setCompanyUuid(uuid) {
      this.companyUuid = uuid
    },

    // ファイル一覧を取得
    async getFiles(path = '', showError = true) {
      // パスとpathPartsを先に設定
      this.currentPath = path
      this.pathParts = path ? path.split('/') : []

      try {
        const response = await apiService.get(
          `/api/storage/${this.companyUuid}/files`,
          {
            path: path,
          },
          {
            showErrorDialog: showError, // エラーダイアログの表示を制御
          }
        )

        this.files = response.files || []

        // サーバーからのcurrentPathが空の場合は、送信したpathを使用
        if (response.currentPath !== undefined && response.currentPath !== '') {
          this.currentPath = response.currentPath
          this.pathParts = response.currentPath ? response.currentPath.split('/') : []
        }

        // 会社名を設定
        this.companyName = response.companyName || ''

        return true
      } catch (error) {
        this.files = []
        return false
      }
    },

    // フォルダ作成
    async createFolder(folderName, moveToNewFolder = true) {
      try {
        const response = await apiService.post(`/api/storage/${this.companyUuid}/folders`, {
          path: this.currentPath,
          name: folderName,
        })
        // 作成したフォルダに移動
        if (moveToNewFolder) {
          const newPath = this.currentPath ? `${this.currentPath}/${folderName}` : folderName
          await this.getFiles(newPath)
        } else {
          // フォルダ作成後はファイル一覧を更新
          await this.getFiles(this.currentPath)
        }
        return response.message
      } catch (error) {
        return null
      }
    },

    // ファイルアップロード（単一ファイル）
    async uploadFile(file) {
      try {
        // ファイルのバリデーション
        if (!file || !(file instanceof File)) {
          return 'アップロードするファイルが選択されていません'
        }

        // ファイルサイズチェック（10MB制限）
        const maxFileSize = 10 * 1024 * 1024 // 10MB
        if (file.size > maxFileSize) {
          return `ファイルサイズが制限を超えています（最大: 10MB、実際: ${this.formatFileSize(file.size)}）`
        }

        const formData = new FormData()
        formData.append('path', this.currentPath || '')
        formData.append('files[]', file)

        const response = await apiService.postFormData(`/api/storage/${this.companyUuid}/upload`, formData, {
          onUploadProgress: progressEvent => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          },
        })

        await this.getFiles(this.currentPath)
        return response.message
      } catch (error) {
        return null
      } finally {
        this.uploadProgress = 0
      }
    },

    // ファイル削除
    async deleteFile(fileName) {
      try {
        const response = await apiService.delete(`/api/storage/${this.companyUuid}/files`, {
          data: {
            path: this.currentPath,
            name: fileName,
          },
        })
        await this.getFiles(this.currentPath)
        return response.message
      } catch (error) {
        return null
      }
    },

    // ファイル名変更
    async renameFile(oldName, newName) {
      try {
        const response = await apiService.post(`/api/storage/${this.companyUuid}/rename`, {
          path: this.currentPath,
          oldName: oldName,
          newName: newName,
        })
        await this.getFiles(this.currentPath)
        return response.message
      } catch (error) {
        return null
      }
    },

    // ファイルダウンロード
    async downloadFile(fileName) {
      try {
        const response = await apiService.getBlob(`/api/storage/${this.companyUuid}/download`, {
          path: this.currentPath ?? '',
          name: fileName,
        })

        if (response && response.data) {
          // Blobデータを直接使用
          const blob = response.data

          // ファイルダウンロード処理
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          return true
        }

        return false
      } catch (error) {
        return false
      }
    },

    // ファイルサイズをフォーマット
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    // 日付をフォーマット
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('ja-JP')
    },

    // ファイルタイプを取得
    getFileType(item) {
      if (!item) return 'ファイル'

      // フォルダの場合
      if (item.type === 'folder') {
        return 'フォルダ'
      }

      // ファイルの場合、拡張子で判定
      if (!item.name) return 'ファイル'
      const extension = item.name.split('.').pop().toLowerCase()
      const typeMap = {
        pdf: 'PDF',
        doc: 'Word',
        docx: 'Word',
        xls: 'Excel',
        xlsx: 'Excel',
        ppt: 'PowerPoint',
        pptx: 'PowerPoint',
        txt: 'テキスト',
        jpg: '画像',
        jpeg: '画像',
        png: '画像',
        gif: '画像',
        zip: '圧縮',
        rar: '圧縮',
      }
      return typeMap[extension] || 'ファイル'
    },

    // パスを移動
    async navigateToPath(path) {
      const result = await this.getFiles(path)
      return result
    },

    // 親フォルダに移動
    async navigateUp() {
      if (this.pathParts.length > 0) {
        const newPath = this.pathParts.slice(0, -1).join('/')
        await this.navigateToPath(newPath)
      }
    },

    // ファイルを選択
    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(f => f.name === file.name)
      if (index >= 0) {
        this.selectedFiles.splice(index, 1)
      } else {
        this.selectedFiles.push(file)
      }
    },

    // 全ファイル選択をクリア
    clearSelection() {
      this.selectedFiles = []
    },

    // ファイル一覧をクリア
    clearFiles() {
      this.files = []
      this.currentPath = ''
      this.pathParts = []
      this.companyName = ''
      this.companyUuid = ''
    },

    // ソート機能
    sortFiles(key) {
      if (this.sortBy === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortOrder = 'asc'
      }

      this.files.sort((a, b) => {
        let aValue = a[key]
        let bValue = b[key]

        if (key === 'size') {
          aValue = a.size || 0
          bValue = b.size || 0
        } else if (key === 'updatedAt') {
          aValue = new Date(a.updatedAt || 0).getTime()
          bValue = new Date(b.updatedAt || 0).getTime()
        } else {
          aValue = String(aValue).toLowerCase()
          bValue = String(bValue).toLowerCase()
        }

        if (this.sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })
    },

    // ===== キューシステム関連のアクション =====

    // キューにタスクを追加
    addToQueue(type, data) {
      const item = new QueueItem(type, data)
      this.taskQueue.push(item)
      this.updateQueueStats()
      this.showQueueUI()
      this.processQueue()
      return item.id
    },

    // 複数タスクを一括追加
    addMultipleToQueue(tasks) {
      const addedIds = []
      tasks.forEach(task => {
        const item = new QueueItem(task.type, task.data)
        this.taskQueue.push(item)
        addedIds.push(item.id)
      })
      this.updateQueueStats()
      this.showQueueUI()
      this.processQueue()
      return addedIds
    },

    // キュー統計を更新
    updateQueueStats() {
      this.queueStats.total = this.taskQueue.length
      this.queueStats.pending = this.taskQueue.filter(t => t.status === QUEUE_STATUS.PENDING).length
      this.queueStats.processing = this.taskQueue.filter(t => t.status === QUEUE_STATUS.PROCESSING).length
      this.queueStats.completed = this.taskQueue.filter(t => t.status === QUEUE_STATUS.COMPLETED).length
      this.queueStats.failed = this.taskQueue.filter(t => t.status === QUEUE_STATUS.ERROR).length
      this.queueStats.cancelled = this.taskQueue.filter(t => t.status === QUEUE_STATUS.CANCELLED).length
    },

    // キューUI表示
    showQueueUI() {
      this.queueUIVisible = true
    },

    // キューUI非表示
    hideQueueUI() {
      this.queueUIVisible = false
      this.queueUIExpanded = false
    },

    // キューUI展開/折りたたみ
    toggleQueueUI() {
      this.queueUIExpanded = !this.queueUIExpanded
    },

    // キュー処理開始
    async processQueue() {
      // 既に処理中の場合は何もしない
      if (this.isQueueProcessing) return

      this.isQueueProcessing = true

      try {
        // 順次処理ループ
        while (true) {
          // 処理可能なタスクがあるかチェック
          if (!this.hasPendingTasks || !this.canProcessMore) {
            break
          }

          const task = this.nextPendingTask
          if (!task) {
            break
          }

          // タスクを同期的に処理（一つずつ確実に）
          await this.processTask(task)
        }
      } finally {
        this.isQueueProcessing = false
        this.checkQueueCompletion()
      }
    },

    // キュー完了チェック
    checkQueueCompletion() {
      // 全てのタスクが完了したかチェック
      if (!this.hasPendingTasks && this.currentProcessingTasks.length === 0) {
        // 全て完了したら少し待ってからUIを非表示
        // setTimeout(() => {
        //   if (this.queueStats.pending === 0 && this.queueStats.processing === 0) {
        //     this.hideQueueUI()
        //   }
        // }, 3000)
      }
    },

    // 個別タスク処理
    async processTask(task) {
      task.status = QUEUE_STATUS.PROCESSING
      task.startedAt = new Date()
      this.currentProcessingTasks.push(task)
      this.updateQueueStats()

      try {
        await this.executeTask(task)
        task.status = QUEUE_STATUS.COMPLETED
        task.completedAt = new Date()
        task.progress = 100
      } catch (error) {
        task.status = QUEUE_STATUS.ERROR
        task.error = error.message || 'Unknown error'

        // エラーの詳細情報を保存
        if (error.response) {
          task.errorDetails = {
            status: error.response.status,
            data: error.response.data,
          }
        }

        // ファイルサイズ制限エラーの場合はリトライしない
        const isFileSizeError = task.error.includes('ファイルサイズが制限を超えています')

        if (!isFileSizeError && this.queueSettings.autoRetry && task.retryCount < this.queueSettings.maxRetries) {
          // リトライは別途処理（processQueueの再帰呼び出しを避ける）
          setTimeout(() => this.retryTask(task), this.queueSettings.retryDelay)
        } else {
          // 最大リトライ回数に達した場合、または制限エラーの場合、エラーダイアログを表示
          this.showTaskErrorDialog(task)
        }
      } finally {
        this.currentProcessingTasks = this.currentProcessingTasks.filter(t => t.id !== task.id)
        this.updateQueueStats()
      }
    },

    // タスクエラーダイアログを表示
    async showTaskErrorDialog(task) {
      const { useDialogStore } = await import('@/stores/dialogStore')
      const dialogStore = useDialogStore()

      let errorMessage = task.error || '不明なエラーが発生しました'

      // タスクタイプに応じたエラーメッセージ
      const taskTypeNames = {
        [QUEUE_TYPES.UPLOAD]: 'アップロード',
        [QUEUE_TYPES.DELETE]: '削除',
        [QUEUE_TYPES.RENAME]: '名前変更',
      }

      const taskTypeName = taskTypeNames[task.type] || '操作'
      const fileName = task.data.file?.name || task.data.fileName || task.data.oldName || '不明なファイル'

      // より詳細なエラーメッセージを作成
      if (task.errorDetails?.data?.message) {
        errorMessage = task.errorDetails.data.message
      }

      await dialogStore.showAlertDialog(`${taskTypeName}エラー`, `「${fileName}」の${taskTypeName}に失敗しました。`)
    },

    // タスク実行
    async executeTask(task) {
      switch (task.type) {
        case QUEUE_TYPES.UPLOAD:
          return await this.executeUploadTask(task)
        case QUEUE_TYPES.DELETE:
          return await this.executeDeleteTask(task)
        case QUEUE_TYPES.RENAME:
          return await this.executeRenameTask(task)
        default:
          throw new Error(`Unknown task type: ${task.type}`)
      }
    },

    // アップロードタスク実行
    async executeUploadTask(task) {
      const { file } = task.data

      // ファイルサイズチェック（10MB制限）
      const maxFileSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxFileSize) {
        throw new Error(`ファイルサイズが制限を超えています（最大: 10MB、実際: ${this.formatFileSize(file.size)}）`)
      }

      const uploadPath = file._path ? file._path : this.currentPath || ''

      const formData = new FormData()
      formData.append('path', uploadPath)
      formData.append('files[]', file)

      const response = await apiService.postFormData(`/api/storage/${this.companyUuid}/upload`, formData, {
        showErrorDialog: false, // キュータスクではエラーダイアログを無効化
        onUploadProgress: progressEvent => {
          task.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        },
      })

      await this.getFiles(this.currentPath) // フォルダ移動に影響しないよう通常通りエラー表示
      return response.message
    },

    // 削除タスク実行
    async executeDeleteTask(task) {
      const { fileName } = task.data

      const response = await apiService.delete(`/api/storage/${this.companyUuid}/files`, {
        showErrorDialog: false, // キュータスクではエラーダイアログを無効化
        data: {
          path: this.currentPath,
          name: fileName,
        },
      })

      await this.getFiles(this.currentPath) // フォルダ移動に影響しないよう通常通りエラー表示
      return response.message
    },

    // 名前変更タスク実行
    async executeRenameTask(task) {
      const { oldName, newName } = task.data

      const response = await apiService.post(
        `/api/storage/${this.companyUuid}/rename`,
        {
          path: this.currentPath,
          oldName: oldName,
          newName: newName,
        },
        {
          showErrorDialog: false, // キュータスクではエラーダイアログを無効化
        }
      )

      await this.getFiles(this.currentPath) // フォルダ移動に影響しないよう通常通りエラー表示
      return response.message
    },

    // タスクリトライ
    async retryTask(task) {
      task.retryCount++
      task.status = QUEUE_STATUS.PENDING
      task.error = null
      task.progress = 0
      this.updateQueueStats()

      // キュー処理を再開（処理中でない場合のみ）
      if (!this.isQueueProcessing) {
        await this.processQueue()
      }
    },

    // タスクキャンセル
    cancelTask(taskId) {
      const task = this.taskQueue.find(t => t.id === taskId)
      if (task && task.status !== QUEUE_STATUS.COMPLETED) {
        task.status = QUEUE_STATUS.CANCELLED
        task.completedAt = new Date()
        this.updateQueueStats()
      }
    },

    // 全タスククリア
    clearCompletedTasks() {
      this.taskQueue = this.taskQueue.filter(t => t.status !== QUEUE_STATUS.COMPLETED && t.status !== QUEUE_STATUS.CANCELLED)
      this.updateQueueStats()
    },

    // キューをクリア
    clearQueue() {
      this.taskQueue = []
      this.currentProcessingTasks = []
      this.isQueueProcessing = false
      this.updateQueueStats()
      this.hideQueueUI()
    },

    // ===== 既存メソッドのキュー対応版 =====

    // 複数ファイルアップロード（キュー対応）
    async uploadMultipleFiles(files) {
      const maxFileSize = 10 * 1024 * 1024 // 10MB
      const allTasks = []

      // 全てのファイルをキューに追加（サイズ制限チェックは実行時に行う）
      Array.from(files).forEach(file => {
        allTasks.push({
          type: QUEUE_TYPES.UPLOAD,
          data: { file },
        })
      })

      // サイズ制限を超えたファイルがあるかチェック
      const oversizedFiles = Array.from(files).filter(file => file.size > maxFileSize)

      if (oversizedFiles.length > 0) {
        const { useDialogStore } = await import('@/stores/dialogStore')
        const dialogStore = useDialogStore()

        const oversizedFilesList = oversizedFiles.map(f => `${f.name} (${this.formatFileSize(f.size)})`).join('<br>')
        const validFilesCount = files.length - oversizedFiles.length

        if (validFilesCount === 0) {
          // 全てのファイルがサイズ制限を超えている場合はアラートで警告して処理中断
          const message = `以下のファイルは全てサイズ制限（10MB）を超えているため、アップロードできません：<br>${oversizedFilesList}`
          await dialogStore.showAlertDialog('ファイルサイズ制限エラー', message)
          return [] // 処理を中断
        } else {
          // 一部のファイルがサイズ制限を超えている場合は確認ダイアログ
          const message = `以下のファイルはサイズ制限（10MB）を超えているため、アップロードに失敗します：<br>${oversizedFilesList}<br><br>有効なファイル（${validFilesCount}件）と合わせて処理を開始しますか？`
          const result = await dialogStore.showConfirmDialog('ファイルサイズ制限', message)
          if (!result) {
            return [] // キャンセルされた場合は何もしない
          }
        }
      }

      // 全てのファイルをキューに追加（制限チェックは実行時に行われる）
      return this.addMultipleToQueue(allTasks)
    },

    // ファイル削除（キュー対応）
    async deleteFileQueued(fileName) {
      return this.addToQueue(QUEUE_TYPES.DELETE, { fileName })
    },

    // ファイル名変更（キュー対応）
    async renameFileQueued(oldName, newName) {
      return this.addToQueue(QUEUE_TYPES.RENAME, { oldName, newName })
    },
  },

  getters: {
    // パンくずリスト用のパス配列を取得
    breadcrumbPaths(state) {
      const paths = [{ name: 'ルート', path: '' }]
      let currentPath = ''

      state.pathParts.forEach((part, index) => {
        currentPath += (index > 0 ? '/' : '') + part
        paths.push({ name: part, path: currentPath })
      })

      return paths
    },

    // フォルダとファイルを分けて表示
    sortedFiles(state) {
      const folders = state.files.filter(f => f.type === 'folder')
      const files = state.files.filter(f => f.type !== 'folder')
      return [...folders, ...files]
    },

    // ===== キューシステム関連のゲッター =====

    // キューの状態別タスク
    pendingTasks: state => state.taskQueue.filter(t => t.status === QUEUE_STATUS.PENDING),
    processingTasks: state => state.taskQueue.filter(t => t.status === QUEUE_STATUS.PROCESSING),
    completedTasks: state => state.taskQueue.filter(t => t.status === QUEUE_STATUS.COMPLETED),
    errorTasks: state => state.taskQueue.filter(t => t.status === QUEUE_STATUS.ERROR),
    cancelledTasks: state => state.taskQueue.filter(t => t.status === QUEUE_STATUS.CANCELLED),

    // 全体進捗
    overallProgress: state => {
      if (state.taskQueue.length === 0) return 0
      const totalProgress = state.taskQueue.reduce((sum, task) => sum + task.progress, 0)
      return Math.round(totalProgress / state.taskQueue.length)
    },

    // キューが空かどうか
    isQueueEmpty: state => state.taskQueue.length === 0,

    // 処理可能かどうか
    canProcessMore: state => state.currentProcessingTasks.length < state.maxConcurrentTasks,

    // 次に処理するタスク
    nextPendingTask: state => state.taskQueue.find(t => t.status === QUEUE_STATUS.PENDING),

    // 待機中のタスクがあるかどうか
    hasPendingTasks: state => state.taskQueue.some(t => t.status === QUEUE_STATUS.PENDING),

    // キューの進行状況サマリー
    queueSummary: state => {
      const total = state.taskQueue.length
      const completed = state.taskQueue.filter(t => t.status === QUEUE_STATUS.COMPLETED).length
      const processing = state.taskQueue.filter(t => t.status === QUEUE_STATUS.PROCESSING).length
      const pending = state.taskQueue.filter(t => t.status === QUEUE_STATUS.PENDING).length
      const failed = state.taskQueue.filter(t => t.status === QUEUE_STATUS.ERROR).length

      return {
        total,
        completed,
        processing,
        pending,
        failed,
        progressText: total > 0 ? `${completed}/${total}` : '0/0',
      }
    },

    // キューのヘッダータイトル
    queueHeaderTitle: state => {
      const total = state.taskQueue.length
      const completed = state.taskQueue.filter(t => t.status === QUEUE_STATUS.COMPLETED).length
      const processing = state.taskQueue.filter(t => t.status === QUEUE_STATUS.PROCESSING).length
      const pending = state.taskQueue.filter(t => t.status === QUEUE_STATUS.PENDING).length

      if (total === 0) return 'ファイル操作'

      if (processing > 0) {
        return `${processing}件のファイルを処理中`
      } else if (pending > 0) {
        return `${pending}件のファイルが待機中`
      } else {
        return `ファイル操作完了 (${total}件)`
      }
    },

    // アクティブなタスク（表示用）
    activeTasks: state => {
      return state.taskQueue
        .filter(t => t.status !== QUEUE_STATUS.COMPLETED && t.status !== QUEUE_STATUS.CANCELLED)
        .sort((a, b) => {
          // 処理中 > 待機中 > エラーの順
          const statusOrder = {
            [QUEUE_STATUS.PROCESSING]: 0,
            [QUEUE_STATUS.PENDING]: 1,
            [QUEUE_STATUS.ERROR]: 2,
          }
          return statusOrder[a.status] - statusOrder[b.status]
        })
    },

    // 完了済みタスク（表示用）
    completedTasksForDisplay: state => {
      return state.taskQueue
        .filter(t => t.status === QUEUE_STATUS.COMPLETED || t.status === QUEUE_STATUS.CANCELLED)
        .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
        .slice(0, 5) // 最新5件のみ表示
    },
  },
})
